import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AgentsState, AgentTask, AgentType, TaskStatus } from './agentsTypes';
import { agentsAPI } from './agentsAPI';

const initialState: AgentsState = {
  agents: [],
  tasks: [],
  currentTask: null,
  status: null,
  loading: false,
  error: null,
};

const agentsSlice = createSlice({
  name: 'agents',
  initialState,
  reducers: {
    setCurrentTask: (state, action: PayloadAction<AgentTask | null>) => {
      state.currentTask = action.payload;
    },
    
    addTask: (state, action: PayloadAction<AgentTask>) => {
      state.tasks.unshift(action.payload);
    },
    
    updateTask: (state, action: PayloadAction<AgentTask>) => {
      const index = state.tasks.findIndex(t => t.task_id === action.payload.task_id);
      if (index !== -1) {
        state.tasks[index] = action.payload;
      }
      if (state.currentTask?.task_id === action.payload.task_id) {
        state.currentTask = action.payload;
      }
    },
    
    clearAgentsError: (state) => {
      state.error = null;
    },
    
    clearTasks: (state) => {
      state.tasks = [];
      state.currentTask = null;
    },
  },
  
  extraReducers: (builder) => {
    // Get Agents Status
    builder.addMatcher(
      agentsAPI.endpoints.getAgentsStatus.matchFulfilled,
      (state, action) => {
        state.status = action.payload;
      }
    );
    
    // List Available Agents
    builder.addMatcher(
      agentsAPI.endpoints.listAvailableAgents.matchFulfilled,
      (state, action) => {
        state.agents = action.payload.agents;
      }
    );
    
    // Orchestrate Agents
    builder.addMatcher(
      agentsAPI.endpoints.orchestrateAgents.matchPending,
      (state) => {
        state.loading = true;
        state.error = null;
      }
    );
    builder.addMatcher(
      agentsAPI.endpoints.orchestrateAgents.matchFulfilled,
      (state, action) => {
        state.loading = false;
        const task: AgentTask = {
          task_id: action.payload.task_id,
          agent_type: (action.payload.agent_type as string).toLowerCase() as AgentType,
          status: action.payload.success ? TaskStatus.COMPLETED : TaskStatus.FAILED,
          requirements: '',
          result: action.payload.result,
          execution_time: action.payload.execution_time,
          error: action.payload.error,
          createdAt: new Date().toISOString(),
          completed_at: new Date().toISOString(),
        };
        state.tasks.unshift(task);
        state.currentTask = task;
      }
    );
    builder.addMatcher(
      agentsAPI.endpoints.orchestrateAgents.matchRejected,
      (state, action: any) => {
        state.loading = false;
        state.error = action.error?.message || 'Orchestration failed';
      }
    );
    
    // Generate Code
    builder.addMatcher(
      agentsAPI.endpoints.generateCode.matchPending,
      (state) => {
        state.loading = true;
        state.error = null;
      }
    );
    builder.addMatcher(
      agentsAPI.endpoints.generateCode.matchFulfilled,
      (state, action) => {
        state.loading = false;
        const task: AgentTask = {
          task_id: action.payload.task_id,
          agent_type: AgentType.CODER,
          status: action.payload.success ? TaskStatus.COMPLETED : TaskStatus.FAILED,
          requirements: '',
          result: action.payload.result,
          execution_time: action.payload.execution_time,
          error: action.payload.error,
          createdAt: new Date().toISOString(),
          completed_at: new Date().toISOString(),
        };
        state.tasks.unshift(task);
        state.currentTask = task;
      }
    );
    builder.addMatcher(
      agentsAPI.endpoints.generateCode.matchRejected,
      (state, action: any) => {
        state.loading = false;
        state.error = action.error?.message || 'Code generation failed';
      }
    );
    
    // Generate Tests
    builder.addMatcher(
      agentsAPI.endpoints.generateTests.matchFulfilled,
      (state, action) => {
        const task: AgentTask = {
          task_id: action.payload.task_id,
          agent_type: AgentType.TESTER,
          status: action.payload.success ? TaskStatus.COMPLETED : TaskStatus.FAILED,
          requirements: '',
          result: action.payload.result,
          execution_time: action.payload.execution_time,
          error: action.payload.error,
          createdAt: new Date().toISOString(),
          completed_at: new Date().toISOString(),
        };
        state.tasks.unshift(task);
      }
    );
    
    // Deploy Project
    builder.addMatcher(
      agentsAPI.endpoints.deployProject.matchFulfilled,
      (state, action) => {
        const task: AgentTask = {
          task_id: action.payload.task_id,
          agent_type: AgentType.DEPLOYER,
          status: action.payload.success ? TaskStatus.COMPLETED : TaskStatus.FAILED,
          requirements: '',
          result: action.payload.result,
          execution_time: action.payload.execution_time,
          error: action.payload.error,
          createdAt: new Date().toISOString(),
          completed_at: new Date().toISOString(),
        };
        state.tasks.unshift(task);
      }
    );
    
    // Get Task Status
    builder.addMatcher(
      agentsAPI.endpoints.getTaskStatus.matchFulfilled,
      (state, action) => {
        state.currentTask = action.payload;
      }
    );
  },
});

export const {
  setCurrentTask,
  addTask,
  updateTask,
  clearAgentsError,
  clearTasks,
} = agentsSlice.actions;

export default agentsSlice.reducer;
